<div class="mb-lg mt-lg" id="select-date-time-container" skeletonScreen [showSkeleton]="isLoading">
    <div
        *ngIf="selectedEmployee"
        hlmAlert
        variant="default"
        class="bg-secondary-50 flex justify-between items-center border border-secondary-200"
        skeletonData
    >
        <div class="flex flex-col">
            <span class="text-xs text-text-secondary">Employee</span>
            <span class="text-md font-medium text-text-primary">{{ selectedEmployee.name }}</span>
        </div>
        <button *ngIf="employees.length > 1" hlmBtn variant="ghost" size="lg" class="bg-white" (click)="onEditEmployee()">Edit</button>
    </div>
    <div *ngIf="!selectedEmployee" hlmAlert variant="default" class="bg-secondary-100 flex justify-between items-center" skeletonData>
        <div class="flex flex-col">
            <span class="text-xs text-text-secondary">Employee</span>
            <span class="text-md text-text-secondary">No employee selected</span>
        </div>
        <button hlmBtn variant="ghost" size="lg" class="bg-white" (click)="onEditEmployee()">Select</button>
    </div>

    <ng-template #loadingTemplate>
        <div class="relative overflow-visible">
            <div skeletonData class="top-6 h-48 right-0 left-0 absolute"></div>
        </div>
    </ng-template>
    <!-- Multi-Day Week View (shown when calendar is closed) -->
    <div *ngIf="calendarPopoverState() === 'closed' && dateList.length > 0; else loadingTemplate" class="mt-6">
        <!-- Month display with calendar icon -->
        <div class="mb-4 flex items-center gap-2">
            <span class="text-lg font-semibold text-text-primary">{{ displayDate | date: 'MMM yyyy' }}</span>
            <button
                hlmBtn
                variant="ghost"
                size="icon"
                class="text-black bg-primary-light hover:bg-primary-light/80 h-8 w-8"
                (click)="onOpenCalendar()"
            >
                <ng-icon hlm size="sm" name="lucideCalendarDays" />
            </button>
        </div>

        <!-- Multi-Day Date Selector -->
        <div class="flex items-center mb-6 relative">
            <!-- Left Navigation Arrow -->
            <button
                hlmBtn
                variant="ghost"
                size="icon"
                class="text-black hover:bg-transparent w-10 h-10 flex-shrink-0"
                [disabled]="shouldDisableLeftChevron"
                (click)="scrollWeekOnClick(HorizontalScrollDirection.Left)"
            >
                <ng-icon hlm size="base" name="lucideChevronLeft" />
            </button>

            <!-- Scrollable Date Container -->
            <div
                class="overflow-x-auto hide-scrollbar horizontal-scroll-box flex-1"
                [horizontalScroll]="'horizontal-scroll-box'"
                #horScrollDirective="horizontalScroll"
                #datesScroller
            >
                <div class="flex gap-2 sm:gap-4">
                    <button
                        *ngFor="let date of displayDateList; let i = index"
                        class="date-block flex-shrink-0 flex flex-col items-center justify-center rounded-lg border transition-all cursor-pointer"
                        [ngClass]="{
                            'bg-primary-base text-white border border-primary-base shadow-md cursor-pointer': isDateSelected(date.date),
                            'bg-primary-light text-text-primary border border-primary-light hover:border-primary-base cursor-pointer':
                                !isDateSelected(date.date) && date.hasAvailabilities,
                            'bg-transparent text-secondary-400 border-0 cursor-default':
                                !isDateSelected(date.date) && !date.hasAvailabilities,
                        }"
                        [disabled]="!date.hasAvailabilities"
                        (click)="onDateBlockClick(date)"
                        (mouseenter)="onDateBlockHover(date.date)"
                        (touchstart)="onDateBlockHover(date.date)"
                        #dateBtn
                    >
                        <span class="text-sm font-medium uppercase"> {{ date.date | date: 'EEE' }} </span>
                        <span class="text-xl my-1"> {{ date.date | date: 'd' }} </span>
                        <span
                            *ngIf="isToday(date.date)"
                            class="inline-block w-2 h-2 rounded-full mt-1"
                            [ngClass]="isDateSelected(date.date) ? 'bg-white' : 'bg-black'"
                        ></span>
                    </button>
                </div>
            </div>

            <!-- Right Navigation Arrow -->
            <button
                hlmBtn
                variant="ghost"
                size="icon"
                class="text-black hover:bg-transparent w-10 h-10 flex-shrink-0"
                [disabled]="shouldDisableRightChevronMaxAdvanceBook"
                (click)="scrollWeekOnClick(HorizontalScrollDirection.Right)"
            >
                <ng-icon hlm size="base" name="lucideChevronRight" />
            </button>
        </div>

        <div *ngIf="isDateWeekLoading" class="h-[150px]" skeletonData [showSkeleton]="isDateWeekLoading"></div>
        <!-- Time-of-Day Section (request-only mode) -->
        <div *ngIf="canShowTimeOfDay" class="flex flex-col gap-3">
            <ng-container *ngIf="isSelectedDateAvailable; else noAvailableTimesCard">
                <button
                    *ngFor="let option of timeOfDayOptions"
                    hlmBtn
                    variant="outline"
                    size="lg"
                    class="time-of-day-button w-full rounded-lg bg-white text-text-primary border-secondary-200 hover:rounded-full hover:bg-primary-base hover:text-white hover:border-primary-base disabled:select-none"
                    [attr.id]="'tod-' + option"
                    [disabled]="isMaxDateTimeOfDayReached()"
                    (click)="onTimeOfDayClick(option)"
                >
                    {{ option }}
                </button>

                <div *ngIf="dateTimeOfDayList.length > 0" class="flex flex-col gap-2 mt-2">
                    <div
                        *ngFor="let entry of dateTimeOfDayList; let i = index"
                        class="flex items-center justify-between p-3 rounded-lg bg-secondary-50 border border-secondary-200"
                    >
                        <div class="flex flex-col">
                            <span class="text-md font-semibold text-text-primary">
                                {{ parseDateTimeOfDayDate(entry.date) | date: 'EEE' }}.
                                {{ parseDateTimeOfDayDate(entry.date) | date: 'MMMM d' }}
                            </span>
                            <span class="text-md text-text-primary">{{ entry.timeOfDay }}</span>
                        </div>
                        <button
                            hlmBtn
                            variant="outline"
                            size="icon"
                            class="border-0 border-transparent text-text-on-primary bg-primary-base shadow-none hover:bg-primary-base/60 hover:text-text-on-primary"
                            [attr.id]="'tod-remove-' + i"
                            (click)="onRemoveDateTimeOfDay(i)"
                        >
                            <ng-icon hlm size="sm" name="lucideTrash2" />
                        </button>
                    </div>
                </div>

                <div *ngIf="dateTimeOfDayList.length < maxDateTimeOfDayEntries" class="text-sm text-text-secondary text-center mt-2">
                    Add up to {{ maxDateTimeOfDayEntries }} times of day that work for you
                </div>
            </ng-container>
        </div>

        <!-- Time Slots Section -->
        <div *ngIf="canShowTimeSlot" class="bg-white rounded-lg bg-secondary-50 p-4">
            <!-- Show time slots if there are availabilities -->
            <div
                *ngIf="morningTimeSlots.length > 0 || afternoonTimeSlots.length > 0 || eveningTimeSlots.length > 0"
                class="grid grid-cols-3 gap-4"
            >
                <!-- Morning Section -->
                <div>
                    <div class="text-md font-semibold text-black mb-3 text-center">Morning</div>
                    <div *ngIf="morningTimeSlots.length" class="flex flex-col gap-2">
                        <button
                            *ngFor="let timeSlot of morningTimeSlots; let i = index"
                            hlmBtn
                            variant="outline"
                            size="lg"
                            class="time-slot-button"
                            [ngClass]="{
                                'bg-primary-base text-white border-primary-base hover:bg-primary-dark': isTimeSlotSelected(
                                    'ts-morning-',
                                    i
                                ),
                                'bg-white text-text-primary border-secondary-200 hover:border-primary-base': !isTimeSlotSelected(
                                    'ts-morning-',
                                    i
                                ),
                            }"
                            id="ts-morning-{{ i }}"
                            (click)="onTimeSlotChange('ts-morning-', i)"
                        >
                            {{ timeSlot | date: 'h:mm a' }}
                        </button>
                    </div>
                    <div *ngIf="!morningTimeSlots.length" class="flex justify-center items-center py-2 rounded-lg bg-secondary-50">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M9.31 18L11.75 15.56L14.19 18L15.25 16.94L12.81 14.5L15.25 12.06L14.19 11L11.75 13.44L9.31 11L8.25 12.06L10.69 14.5L8.25 16.94L9.31 18ZM19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20Z"
                                fill="#5D556D"
                            />
                        </svg>
                    </div>
                </div>

                <!-- Afternoon Section -->
                <div>
                    <div class="text-md font-semibold text-black mb-3 text-center">Afternoon</div>
                    <div *ngIf="afternoonTimeSlots.length" class="flex flex-col gap-2">
                        <button
                            *ngFor="let timeSlot of afternoonTimeSlots; let i = index"
                            hlmBtn
                            variant="outline"
                            size="lg"
                            class="time-slot-button"
                            [ngClass]="{
                                'bg-primary-base text-white border-primary-base hover:bg-primary-dark': isTimeSlotSelected(
                                    'ts-afternoon-',
                                    i
                                ),
                                'bg-white text-text-primary border-secondary-200 hover:border-primary-base': !isTimeSlotSelected(
                                    'ts-afternoon-',
                                    i
                                ),
                            }"
                            id="ts-afternoon-{{ i }}"
                            (click)="onTimeSlotChange('ts-afternoon-', i)"
                        >
                            {{ timeSlot | date: 'h:mm a' }}
                        </button>
                    </div>
                    <div *ngIf="!afternoonTimeSlots.length" class="flex justify-center items-center py-2 rounded-lg bg-secondary-50">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M9.31 18L11.75 15.56L14.19 18L15.25 16.94L12.81 14.5L15.25 12.06L14.19 11L11.75 13.44L9.31 11L8.25 12.06L10.69 14.5L8.25 16.94L9.31 18ZM19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20Z"
                                fill="#5D556D"
                            />
                        </svg>
                    </div>
                </div>

                <!-- Evening Section -->
                <div>
                    <div class="text-md font-semibold text-black mb-3 text-center">Evening</div>
                    <div *ngIf="eveningTimeSlots.length" class="flex flex-col gap-2">
                        <button
                            *ngFor="let timeSlot of eveningTimeSlots; let i = index"
                            hlmBtn
                            variant="outline"
                            size="lg"
                            class="time-slot-button"
                            [ngClass]="{
                                'bg-primary-base text-white border-primary-base hover:bg-primary-dark': isTimeSlotSelected(
                                    'ts-evening-',
                                    i
                                ),
                                'bg-white text-text-primary border-secondary-200 hover:border-primary-base': !isTimeSlotSelected(
                                    'ts-evening-',
                                    i
                                ),
                            }"
                            id="ts-evening-{{ i }}"
                            (click)="onTimeSlotChange('ts-evening-', i)"
                        >
                            {{ timeSlot | date: 'h:mm a' }}
                        </button>
                    </div>
                    <div *ngIf="!eveningTimeSlots.length" class="flex justify-center items-center py-2 rounded-lg bg-secondary-50">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M9.31 18L11.75 15.56L14.19 18L15.25 16.94L12.81 14.5L15.25 12.06L14.19 11L11.75 13.44L9.31 11L8.25 12.06L10.69 14.5L8.25 16.94L9.31 18ZM19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20Z"
                                fill="#5D556D"
                            />
                        </svg>
                    </div>
                </div>
            </div>

            <!-- Show "No Available Times" when selected date has no availabilities -->
            <ng-container *ngIf="morningTimeSlots.length === 0 && afternoonTimeSlots.length === 0 && eveningTimeSlots.length === 0">
                <ng-container *ngTemplateOutlet="noAvailableTimesCard"></ng-container>
            </ng-container>
        </div>
    </div>

    <!-- Shared "No Available Times" content, used by both the time-slot and time-of-day sections -->
    <ng-template #noAvailableTimesCard>
        <div class="flex flex-col items-center justify-center py-8 px-4 rounded-lg bg-secondary-50">
            <ng-icon hlm name="lucideCalendarX" class="text-text-secondary mb-4" style="font-size: 24px; width: 24px; height: 24px" />
            <div class="text-md font-semibold text-text-primary mb-2">No Available Times</div>
            <ng-container *ngIf="getNextAvailableDate(); else noFutureAvailability">
                <div class="text-sm text-text-primary text-center mb-4">
                    The next available appointment is<br />
                    <span class="text-sm font-semibold text-text-primary">{{ getNextAvailableDate() | date: 'EEEE, MMMM d, yyyy' }}</span>
                </div>
                <button
                    hlmBtn
                    variant="default"
                    size="lg"
                    class="bg-primary-base text-white hover:bg-primary-dark"
                    (click)="goToNextAvailable()"
                >
                    <ng-icon hlm size="sm" name="lucideChevronsRight" class="mr-2" />
                    Go to next available
                </button>
                <div class="text-sm font-bold text-text-primary mt-4">Or select another employee above</div>
            </ng-container>
            <ng-template #noFutureAvailability>
                <div class="text-sm font-bold text-text-primary mt-4">Please select another employee above</div>
            </ng-template>
        </div>
    </ng-template>

    <!-- Select Time Popover (Month Calendar View) -->
    <brn-popover [state]="calendarPopoverState()" (stateChanged)="calendarPopoverState.set($event)">
        <!-- Hidden trigger button -->
        <button type="button" class="w-full h-0 opacity-0 pointer-events-none focus:outline-none" brnPopoverTrigger></button>

        <div hlmPopoverContent class="w-full p-3 relative" *brnPopoverContent="let ctx">
            <!-- Close Button (positioned top-right) -->
            <button
                *ngIf="isCloseButtonVisible"
                hlmBtn
                variant="ghost"
                size="sm"
                class="absolute top-2 right-3 text-primary-base hover:text-primary-dark cursor-pointer px-3 py-2 z-10 focus:outline-none"
                brnPopoverClose
                (click)="onCloseCalendar()"
            >
                Close
            </button>

            <!-- Calendar Component -->
            <hlm-calendar
                [(date)]="calendarDisplayDate"
                (dateChange)="onDateChange($event)"
                [min]="minDate"
                [max]="maxDate"
                [dateDisabled]="filterDates"
                captionLayout="label"
                weekdayFormat="narrow"
                [cellSize]="42"
                availableDateBg="bg-primary-light"
                availableDateText="text-black"
                availableDateHoverBg="bg-primary-base"
                availableDateHoverText="text-white"
                selectedDateBg="bg-primary-base"
                selectedDateText="text-white"
                calendarClass="border-0"
            />

            <!-- No Availability Section (shown when hasNoAvailability is true) -->
            <div *ngIf="hasNoAvailability" class="mt-6 border-t border-secondary-200 pt-6">
                <div class="text-center mb-4">
                    <h3 class="text-lg font-semibold text-text-primary mb-2">No Availability This Month</h3>
                    <p class="text-sm text-text-secondary">
                        The next available day is<br />
                        <span class="font-medium text-text-primary">{{ nextAvailableDate | date: 'MMMM d, yyyy' }}</span>
                    </p>
                </div>
                <button
                    hlmBtn
                    variant="outline"
                    size="lg"
                    class="w-full text-primary-base border-primary-base hover:bg-primary-light"
                    (click)="onGoToNextAvailable()"
                >
                    <ng-icon hlm size="sm" name="lucideChevronsRight" class="mr-2" />
                    Go to Next Available
                </button>
            </div>
        </div>
    </brn-popover>
</div>

<!-- Employee Selection Sheet -->
<hlm-sheet #employeeSelection side="bottom" [closeOnBackdropClick]="false" [closeOnOutsidePointerEvents]="false">
    <hlm-sheet-content *brnSheetContent="let ctx" class="!px-3 !pt-3 flex flex-col">
        <!-- Scrollable Employee List -->
        <div class="flex-1 overflow-y-auto">
            <app-select-employee
                [employeeList]="employees"
                [services]="selectedServices"
                (assignSelectedEmployee)="assignSelectedEmployeeInSheet($event)"
            ></app-select-employee>
        </div>

        <hlm-sheet-footer class="text-center !px-0 pt-0">
            <button hlmBtn type="button" variant="default" class="text-md" (click)="onUpdateEmployeeFromSheet()">Update Employee</button>
        </hlm-sheet-footer>
    </hlm-sheet-content>
</hlm-sheet>
